// Specimen metadata set in the somatic wizard (roadmap R1.2a).
//
// The start forms ask only the specimen types: they cannot be inferred and they
// change how the analysis is read (FFPE, tissue vs blood, cell line). Collection
// dates, sex, age and the pathologist's tumour % are not asked at analysis start
// (nothing in the run reads them; purity is computed). Values an analysis already
// holds are still described on its pages. The vocabulary mirrors libra
// somatic_patient.SPECIMEN_TYPES and SEXES; the backend validates again. A blank
// field is not sent and reads "not recorded", never as a negative. Plasma and
// cfDNA are deliberately absent (liquid biopsy is out of scope).

export const SPECIMEN_TYPES = [
  { value: 'ffpe', label: 'FFPE tissue' },
  { value: 'fresh_frozen', label: 'Fresh frozen tissue' },
  { value: 'fresh_tissue', label: 'Fresh tissue' },
  { value: 'blood', label: 'Peripheral blood' },
  { value: 'bone_marrow', label: 'Bone marrow' },
  { value: 'saliva', label: 'Saliva' },
  { value: 'buccal', label: 'Buccal swab' },
  { value: 'normal_tissue', label: 'Normal tissue' },
  { value: 'cell_line', label: 'Cell line' },
  { value: 'other', label: 'Other' },
]

export const SEXES = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
]

export const MAX_AGE_YEARS = 130

export const EMPTY_SPECIMEN = {
  tumourType: '',
  tumourPercent: '',
  tumourCollectionDate: '',
  normalType: '',
  normalCollectionDate: '',
  sex: '',
  ageYears: '',
}

export const specimenTypeLabel = (value) => SPECIMEN_TYPES.find((t) => t.value === value)?.label || value

const blank = (value) => value === undefined || value === null || String(value).trim() === ''

// Only the specimen types are entered, both picked from a list, so there is
// nothing to check; kept so callers need not change.
// eslint-disable-next-line no-unused-vars
export const validateSpecimen = (specimen = EMPTY_SPECIMEN) => ({})

// The `annotation.specimen` payload: the specimen types only, or undefined when
// none was chosen. A tumour-only run never sends a normal block. Keys the form no
// longer asks (an old draft may hold them) are not sent.
export const buildSpecimenPayload = (specimen = EMPTY_SPECIMEN, { paired = false } = {}) => {
  const out = {}
  if (!blank(specimen.tumourType)) out.tumour = { specimenType: specimen.tumourType }
  if (paired && !blank(specimen.normalType)) out.normal = { specimenType: specimen.normalType }
  return Object.keys(out).length ? out : undefined
}

// One line for the review step and the analysis page: what was recorded, and
// "not recorded" for the rest, so an absent value never reads as a result.
export const describeSpecimen = ({ specimenType, tumourPercent, collectionDate } = {}, { tumour = true } = {}) => {
  const parts = [blank(specimenType) ? 'type not recorded' : specimenTypeLabel(specimenType)]
  if (tumour) {
    parts.push(blank(tumourPercent) ? 'tumour % not recorded' : `${Number(tumourPercent)}% tumour (pathologist)`)
  }
  if (!blank(collectionDate)) parts.push(`collected ${collectionDate}`)
  return parts.join(', ')
}

// "Female, 54 years"; each missing part says so.
export const describePatient = ({ sex, ageYears } = {}) => {
  const sexText = blank(sex) ? 'sex not recorded' : SEXES.find((x) => x.value === sex)?.label || sex
  const ageText = blank(ageYears) ? 'age not recorded' : `${Number(ageYears)} years`
  return `${sexText}, ${ageText}`
}
